import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { AssignAllShiftsButtonService } from '../../timeline-unassigned-region/assign-all-shifts-button-view/assign-all-shifts-button.service';
import { AssignmentOptimizerFormComponent } from './assignment-optimizer-settings-form/assignment-optimizer-form.component';
import { ShiftOptimizerGatewayService } from '@wilson/api/gateway';
import { AssignmentOptimizerSetting } from '@wilson/interfaces';
import { catchError, finalize, map, Observable, of } from 'rxjs';
import { captureException } from '@sentry/angular';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TransformMasterDataTranslationService } from '@wilson/non-domain-specific/master-data-translate';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { LastUpdateStatusComponent } from '@wilson/non-domain-specific/drawer-common';

@Component({
  selector: 'wilson-assignment-optimizer-settings-drawer',
  imports: [
    CommonModule,
    TranslateModule,
    NzDrawerModule,
    NzButtonModule,
    AssignmentOptimizerFormComponent,
    NzSkeletonModule,
    LastUpdateStatusComponent,
  ],
  templateUrl: './assignment-optimizer-settings-drawer.component.html',
  styleUrls: ['./assignment-optimizer-settings-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignmentOptimizerSettingsComponent implements OnInit {
  protected assignAllShiftsButtonService = inject(AssignAllShiftsButtonService);
  protected shiftOptimizerGatewayService = inject(ShiftOptimizerGatewayService);
  protected translate = inject(TranslateService);
  protected nzMessageService = inject(NzMessageService);
  protected transformMasterDataTranslationService = inject(
    TransformMasterDataTranslationService,
  );

  protected optimizerSettings$: Observable<
    | (AssignmentOptimizerSetting & {
        translatedName: string;
        translatedDescription: string;
      })[]
    | null
  > = of(null);

  protected loadingSignal = signal(false);
  protected updatedAt = signal<string>('');
  protected isSuccessfullySaved = signal(false);
  ngOnInit(): void {
    this.fetchAssignmentOptimizerSettings();
  }

  protected async updateIsSelectedStateOfSettings(
    payload: Partial<AssignmentOptimizerSetting>,
  ): Promise<void> {
    this.isSuccessfullySaved.set(false);
    if (payload) {
      try {
        await this.updateOptimizerSettings(payload);
        if ('isSelected' in payload) {
          this.fetchAssignmentOptimizerSettings();
        } else {
          this.updatedAt.set(new Date().toISOString());
          this.isSuccessfullySaved.set(false);
        }
      } catch (error) {
        console.error('Error updating settings:', error);
        this.nzMessageService.error(this.translate.instant('general.error'));
      }
    }
  }

  private async updateOptimizerSettings(
    payload: Partial<AssignmentOptimizerSetting>,
  ): Promise<void> {
    this.nzMessageService.loading(
      this.translate.instant('general.loading', { nzDuration: 0 }),
    );

    try {
      await this.shiftOptimizerGatewayService.patchAssignmentOptimizerSettings(
        payload,
      );
    } catch (error) {
      captureException(error);
      this.nzMessageService.error(this.translate.instant('general.error'));
      this.fetchAssignmentOptimizerSettings();
    }

    this.nzMessageService.remove();
  }

  private fetchAssignmentOptimizerSettings(): void {
    this.loadingSignal.set(true);
    this.optimizerSettings$ = this.shiftOptimizerGatewayService
      .getAssignmentOptimizerSettings()
      .pipe(
        map(
          (
            value,
          ):
            | (AssignmentOptimizerSetting & {
                translatedName: string;
                translatedDescription: string;
              })[]
            | null => {
            if (value) {
              this.updatedAt.set(value[0].updatedAt);
              const withTranslatedName =
                this.transformMasterDataTranslationService.transform(value);
              return this.transformMasterDataTranslationService.transformDescriptions(
                withTranslatedName,
              );
            } else {
              return null;
            }
          },
        ),
        catchError((error) => {
          captureException(error);
          this.nzMessageService.error(this.translate.instant('general.error'));
          this.assignAllShiftsButtonService.renderAssignmentOptimizerDrawer(
            false,
          );
          return of(null);
        }),
        finalize(() => {
          this.loadingSignal.set(false);
        }),
      );
  }
}
